import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { AssetWithType, CreateTransactionRequest } from '../types';

interface TransactionFormProps {
  preSelectedAssetId?: number;
  onSuccess?: () => void;
}

const TransactionForm: React.FC<TransactionFormProps> = ({ preSelectedAssetId, onSuccess }) => {
  const [assets, setAssets] = useState<AssetWithType[]>([]);
  const [formData, setFormData] = useState<CreateTransactionRequest>({
    asset_id: preSelectedAssetId || 0,
    transaction_type: 'BUY',
    quantity: 0,
    price_per_unit: 0,
    transaction_date: new Date().toISOString().split('T')[0],
    fees: 0,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (preSelectedAssetId) {
      setFormData((prev) => ({ ...prev, asset_id: preSelectedAssetId }));
    }
  }, [preSelectedAssetId]);

  const fetchAssets = async () => {
    try {
      const data = await apiClient.getAssets();
      setAssets(data);
    } catch (err) {
      console.error('Error fetching assets:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (formData.asset_id === 0) {
      setError('Please select an asset');
      return;
    }

    try {
      setLoading(true);
      await apiClient.createTransaction(formData);
      
      // Reset form
      setFormData({
        asset_id: preSelectedAssetId || 0,
        transaction_type: 'BUY',
        quantity: 0,
        price_per_unit: 0,
        transaction_date: new Date().toISOString().split('T')[0],
        fees: 0,
        notes: '',
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
      console.error('Error creating transaction:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedAsset = assets.find((a) => a.id === formData.asset_id);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Asset</label>
        <select
          value={formData.asset_id}
          onChange={(e) => setFormData({ ...formData, asset_id: parseInt(e.target.value) })}
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
          required
          disabled={!!preSelectedAssetId}
        >
          <option value={0}>Select an asset...</option>
          {assets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.symbol} - {asset.name} ({asset.asset_type.name})
            </option>
          ))}
        </select>
        {selectedAsset && (
          <p className="mt-1 text-xs text-slate-400">
            Type: {selectedAsset.asset_type.name} ({selectedAsset.asset_type.category})
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Transaction Type</label>
        <div className="flex gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              value="BUY"
              checked={formData.transaction_type === 'BUY'}
              onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
              className="w-4 h-4 text-[var(--accent-gold)] focus:ring-[var(--accent-gold)]"
            />
            <span className="text-green-400 font-medium">BUY</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              value="SELL"
              checked={formData.transaction_type === 'SELL'}
              onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
              className="w-4 h-4 text-[var(--accent-gold)] focus:ring-[var(--accent-gold)]"
            />
            <span className="text-red-400 font-medium">SELL</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Quantity</label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={formData.quantity || ''}
            onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Price per Unit (₹)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price_per_unit || ''}
            onChange={(e) => setFormData({ ...formData, price_per_unit: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Transaction Date</label>
          <input
            type="date"
            value={formData.transaction_date}
            onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Fees (₹) - Optional</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.fees || ''}
            onChange={(e) => setFormData({ ...formData, fees: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Notes - Optional</label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any additional notes..."
          rows={3}
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)] resize-none"
        />
      </div>

      {formData.quantity > 0 && formData.price_per_unit > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Total Amount:</span>
            <span className="text-xl font-bold text-white">
              ₹{(formData.quantity * formData.price_per_unit + (formData.fees || 0)).toFixed(2)}
            </span>
          </div>
          {formData.fees && formData.fees > 0 && (
            <p className="text-xs text-slate-500 mt-1">
              (₹{(formData.quantity * formData.price_per_unit).toFixed(2)} + ₹{formData.fees.toFixed(2)} fees)
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full px-4 py-3 bg-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/80 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Creating...' : `${formData.transaction_type} ${selectedAsset?.symbol || 'Asset'}`}
      </button>
    </form>
  );
};

export default TransactionForm;